import React from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';
import { FileText, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import useFinanceData from './useFinanceData';

/**
 * Finance — onglet P&L (compte de résultat). Présente le détail des
 * produits / charges avec 3 perspectives (today / cumul / fin-mois),
 * graphe de répartition et liste des aléas exceptionnels du mois.
 * Extracted from Finance.jsx on 28/05/2026.
 */
export default function PnlTab() {
  const { t } = useTranslation();
  const {
    month, year, monthlyRevenues, dayOfMonth, totalMonthlyCharges,
    barData, netResult, monthEventLog, monthlyExceptional, monthlyExceptionalGains,
  } = useFinanceData();

  return (
    <div className="space-y-4">
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> {t('income_statement')} ({t('month')} {month}, {t('year')} {year})
        </h3>

        {/* Produits */}
        <div className="mb-4">
          <p className="text-xs font-bold text-money uppercase tracking-wider mb-2">{t('products')}</p>
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">{t('revenue_contracts')}</span>
              <span className="font-medium text-money">{formatMoney(monthlyRevenues)}</span>
            </div>
          </div>
          <div className="flex justify-between text-sm font-bold border-t border-money/40 pt-2 mt-2">
            <span className="text-money">{t('total_products')}</span>
            <span className="text-money">{formatMoney(monthlyRevenues)}</span>
          </div>
        </div>

        {/* Charges — table with 3 perspectives */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-danger uppercase tracking-wider">{t('charges')}</p>
            <p className="text-[9px] text-slate-500 font-mono">J{dayOfMonth}/30</p>
          </div>
          <table className="w-full text-xs" data-testid="charges-3col-table">
            <thead>
              <tr className="text-[10px] text-slate-500 uppercase tracking-wider border-b border-ink-600">
                <th className="text-left py-1.5 font-mono">Poste</th>
                <th className="text-right py-1.5 font-mono" title="Charge journalière lissée (= mensuel ÷ 22 jours ouvrés)">Auj.</th>
                <th className="text-right py-1.5 font-mono" title="Cumul depuis le 1er du mois">Cumul</th>
                <th className="text-right py-1.5 font-mono" title="Projection fin du mois (sans changement de paramètres)">Fin mois</th>
              </tr>
            </thead>
            <tbody>
              {barData.map(item => (
                <tr key={item.name} className="border-b border-ink-600/30" data-testid={`charge-row-${item.name}`}>
                  <td className="py-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-slate-400 truncate">{item.name}</span>
                    </div>
                  </td>
                  <td className="text-right py-1 font-mono text-slate-300">{formatMoney(item.today)}</td>
                  <td className="text-right py-1 font-mono text-slate-300">{formatMoney(item.cumul)}</td>
                  <td className="text-right py-1 font-mono text-slate-200">{formatMoney(item.projection)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-sm font-bold border-t-2 border-danger/40">
                <td className="text-danger py-2">{t('total_charges')}</td>
                <td className="text-right py-2 font-mono text-slate-300">{formatMoney(totalMonthlyCharges / 22)}</td>
                <td className="text-right py-2 font-mono text-slate-300">{formatMoney(totalMonthlyCharges * (dayOfMonth / 30))}</td>
                <td className="text-right py-2 font-mono text-danger">{formatMoney(totalMonthlyCharges)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="text-[9px] text-slate-500 mt-1 italic">
            <strong>Auj.</strong> = charge journalière lissée · <strong>Cumul</strong> = depuis le 1<sup>er</sup> du mois · <strong>Fin mois</strong> = projection si tout reste stable
          </p>
        </div>

        {/* Résultat */}
        <div className={`rounded-lg p-3 text-center ${netResult >= 0 ? 'bg-money/10' : 'bg-danger/10'}`}>
          <p className="text-xs text-slate-500">{t('net_monthly_result')}</p>
          <p className={`text-2xl font-bold ${netResult >= 0 ? 'text-money' : 'text-danger'}`}>
            {netResult >= 0 ? '+' : ''}{formatMoney(netResult)}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">
            {t('margin')}: {monthlyRevenues > 0 ? `${((netResult / monthlyRevenues) * 100).toFixed(1)}%` : '—'}
          </p>
        </div>
      </div>

      {/* Charges Breakdown Chart */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">{t('charges_breakdown')}</h3>
        {barData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={barData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#9CA3AF" width={60} />
                <Tooltip formatter={(v) => [formatMoney(v), 'Charge']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="value" fill="#1F3864" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>

            {/* Visual proportions */}
            <div className="mt-3 space-y-1.5">
              {barData.map(item => (
                <div key={item.name} className="flex items-center gap-2 text-xs">
                  <span className="w-14 text-slate-500 text-right">{item.name}</span>
                  <div className="flex-1 h-3 rounded-full bg-ink-700/40 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${(item.value / totalMonthlyCharges) * 100}%`, backgroundColor: item.color }}
                    />
                  </div>
                  <span className="w-12 text-right text-slate-500">{((item.value / totalMonthlyCharges) * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">{t('no_active_charge')}</p>
        )}
      </div>

      {/* Exceptional charges (events) — current month */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="exceptional-charges-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-danger" />
            Charges exceptionnelles — Aléas du mois {month}
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">[{monthEventLog.length}]</span>
        </div>
        {monthEventLog.length === 0 ? (
          <p className="text-xs text-slate-500 italic text-center py-3">Aucun aléa résolu ce mois-ci.</p>
        ) : (
          <>
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {monthEventLog.map((e, idx) => {
                const net = (e.eventBonus || 0) - (e.eventCost || 0);
                return (
                  <div key={`${e.id}-${idx}`} className="border border-ink-600 bg-ink-700/30 rounded-md px-3 py-2 flex items-start gap-2" data-testid={`event-log-row-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-200 truncate">{e.name}</p>
                      <p className="text-[10px] text-slate-500">
                        J{e.resolvedAt?.day} · {e.choiceMade}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-xs font-bold font-mono ${net >= 0 ? 'text-money' : 'text-danger'}`}>
                        {net >= 0 ? '+' : ''}{formatMoney(net)}
                      </p>
                      {(e.eventCost || 0) > 0 && (e.eventBonus || 0) > 0 && (
                        <p className="text-[9px] text-slate-500">
                          -{formatMoney(e.eventCost)} +{formatMoney(e.eventBonus)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-2 border-t border-ink-600 flex items-center justify-between text-xs">
              <span className="text-slate-400">Solde des aléas du mois</span>
              <span className={`font-bold font-mono ${(monthlyExceptionalGains - monthlyExceptional) >= 0 ? 'text-money' : 'text-danger'}`}>
                {(monthlyExceptionalGains - monthlyExceptional) >= 0 ? '+' : ''}{formatMoney(monthlyExceptionalGains - monthlyExceptional)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
